import React, { useEffect, useRef, useState } from "react";
import Divider from "@mui/material/Divider";
import { EstadoAgenteChip, FormButton, SpinnerOverlay } from "components";
import { FormikSelectInput } from "components/UI/Inputs/FormikInputs";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";
import styles from "styles/pages/ModalActions.module.scss";
import { ESTADOS_CONEXION } from "../consts";
import { RNHAgenteUpd } from "services/services-mia/programa-salud/personal/RNHAgenteUpd";
import { RNHAgenteGet } from "services/services-mia/programa-salud/personal/RNHAgenteGet";

/**
 * Cambio de estado de un agente, desde el listado.
 *
 * Manda SOLO el estado: para RNHAgenteUpd, omitir un campo significa "no cambiar".
 *
 * Pide el detalle al abrir porque necesita `rnhAgenteVersion`, y **la respuesta del
 * listado no la trae**. Sin ella el Upd responde 41004 y no graba nada.
 *
 * OJO con lo que hace el estado "B": da de baja al agente y, en la misma operación,
 * le quita TODOS sus lugares. No es solo un cambio de etiqueta.
 */
export default function EstadoModal({ row, updateList, closeModal }) {
  const setSnackBarInfo = useSetRecoilState(snackbarData);
  const credentials = useRecoilValue(userCredentials);
  const [inRequest, setInRequest] = useState(false);
  const formikRef = useRef();

  const estadoActual = row?.statusCod ?? "";
  const [version, setVersion] = useState(null);

  useEffect(() => {
    (async () => {
      if (!row?.id) return;
      setInRequest(true);
      const { data } = await RNHAgenteGet(credentials, { agenteId: row.id });
      setInRequest(false);
      setVersion(data?.[0]?.rnhAgenteVersion ?? null);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const opciones = ESTADOS_CONEXION.filter(({ cod }) => cod !== estadoActual);

  const cambiarEstado = async (values) => {
    setInRequest(true);

    const response = await RNHAgenteUpd(credentials, {
      agenteId: row?.id,
      version,
      statusCod: values.status,
    });
    setInRequest(false);

    if (!response.ok) return;

    setSnackBarInfo({
      message: "Estado modificado correctamente",
      severity: "success",
      open: true,
    });
    formikRef.current.resetForm();
    updateList();
    closeModal();
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{ status: "" }}
      validationSchema={yup.object({
        status: yup.string().required("Debe elegir un estado nuevo"),
      })}
      onSubmit={(values) => {
        cambiarEstado(values);
      }}
    >
      <Form className={styles.inputs} noValidate>
        <div
          className={styles["column"]}
          style={{ width: "420px", maxWidth: "100%" }}
        >
          <div style={{ width: "100%" }}>
            <div
              style={{
                alignItems: "center",
                display: "flex",
                gap: "1rem",
                justifyContent: "space-around",
              }}
            >
              <div>
                <h3 style={{ fontSize: "1.15rem", fontWeight: 600, margin: 0 }}>
                  {row?.apellidosNombres}
                </h3>
                <p
                  style={{
                    color: "#5F6368",
                    margin: "0.25rem 0 0",
                    textAlign: "center",
                  }}
                >
                  {row?.cargo || "Sin cargo"}
                </p>
              </div>
              <EstadoAgenteChip cod={estadoActual} />
            </div>

          </div>

          <Divider sx={{ margin: "0.25rem 0 0.75rem", width: "100%" }} />

          <FormikSelectInput fullWidth={true} name="status" labelText="Nuevo estado">
            <option value="" disabled>
              Seleccione
            </option>
            {opciones.map(({ cod, label }) => (
              <option key={cod} value={cod}>
                {label}
              </option>
            ))}
          </FormikSelectInput>
          <FormButton
            inRequest={inRequest}
            submitDisabled={version == null}
            newFormData={{ button: "Guardar" }}
            onCloseModal={closeModal}
          />
          {inRequest && <SpinnerOverlay />}
        </div>
      </Form>
    </Formik>
  );
}
